/*
 * SPD v5.92 · Governance Tile · Live Session compositor.
 *
 * Live tile on the Admin Dashboard. Mounted above Security Health.
 *
 * All copy is operator-approved via mockup v8.8.12:
 *   [COPY-APPROVED: governance-tile-v8]  (2026-07-24)
 *
 * IJ-081 · 2026-07-24 · Phase 1 · Header row port.
 * IJ-082 · 2026-07-24 · Phase 2 · Numbers-reconcile block port.
 * IJ-083 · 2026-07-24 · Phase 3 · How E1 is going column.  Live-wired
 *   to /api/admin/governance-tile/summary — every measured metric
 *   binds to the endpoint response; every unmeasured field renders
 *   an em dash with a "not yet measured" tooltip so the tile can
 *   never violate its own attestation-honesty red-line rule.
 * IJ-084 · 2026-07-24 · Phase 4 · How the Judge is going column.
 *   Same live-wire pattern.  Bound to judge_missing_reasoning_count,
 *   avg_check_time_seconds, judge_proactive_catches, tasks_total,
 *   judge_false_negatives.  Every per-sub-parameter Judge score and
 *   every Judge session-start ritual sub-check renders as em dash
 *   pending IJ-034 / IJ-045.  Operator column and queries block
 *   remain as placeholders until IJ-085 · IJ-086 · IJ-087 land.
 * IJ-085 · 2026-07-24 · Phase 5 · How the Operator is going column.
 *   Same live-wire pattern.  Headline bound to got_wrong / tasks_total.
 *   Status chip three-branched: 'System quiet' when got_wrong = 0,
 *   'System-load · N catches' amber otherwise, em dash while summary
 *   is loading.  Section 1 total bound to got_wrong; the seven
 *   breakdown rows remain pending IJ-034 / IJ-045.  Inputs section:
 *   copy-approved binds to words_approved; other inputs pending.
 *   Signals section: fully pending.  Direction-of-travel chart and
 *   severity counters remain as placeholders until IJ-086 · IJ-087
 *   land.
 * IJ-086 · 2026-07-24 · Phase 6 · Direction-of-travel chart.  SVG
 *   axis shell + Y grid + X grid + zero baseline + Y-axis rotated
 *   labels + read-me explainer banner render unconditionally.  For
 *   every entry in CHART_SERIES that carries non-zero per-turn data
 *   under `summary.per_turn_events[series.key]`, a cumulative
 *   polyline + labelled endpoint dot draws.  When the endpoint does
 *   not yet emit per-turn arrays (current state pending IJ-034 /
 *   IJ-045) the plot area overlays an empty-state message instead
 *   of fabricating lines from aggregate totals (attestation-honesty
 *   rule · anchor GB-013).  X-axis range computes live from
 *   tasks_total so no hardcoded "48" reaches the DOM.  Severity
 *   counters + task cards remain as placeholder until IJ-087 lands.
 * IJ-087 · 2026-07-24 · Phase 7 · Severity counters + task-card
 *   grid.  Section-title turn count binds live to tasks_total; four
 *   Sev counters (Sev 0..3) render em dash with `-pending` testid
 *   marker (pending IJ-034 / IJ-045); task-card grid renders an
 *   empty-state overlay when the endpoint does not yet emit
 *   `task_cards[]`, else iterates the array with a scaffolded
 *   card renderer.  Live-tile port COMPLETE — no phase placeholders
 *   remain in the compositor.
 * IJ-088 · 2026-07-24 · Stale "48" purge — every hardcoded 48 removed
 *   from render path; NumbersReconcile now consumes summary/loading/
 *   error props for full live-wire.
 *
 * Refactor per MG-02 C1: every file in this folder is under 500 lines.
 * See ./Header.jsx · ./NumbersReconcile.jsx · ./columns/E1Column.jsx.
 */
import Header from './Header';
import NumbersReconcile from './NumbersReconcile';
import NightlyRunAlert from './NightlyRunAlert';
import E1Column from './columns/E1Column';
import JudgeColumn from './columns/JudgeColumn';
import OperatorColumn from './columns/OperatorColumn';
import DirectionChart from './DirectionChart';
import TasksBlock from './TasksBlock';
import useTileData from './hooks/useTileData';

function GovernanceTile() {
  const { view, setView, zeroCounters, todayISO, summary, loading, error } = useTileData();

  return (
    <section
      data-testid="governance-tile-root"
      className="rounded-xl border border-slate-800/70 bg-slate-950/60 p-6 shadow-lg"
    >
      <Header
        view={view}
        setView={setView}
        zeroCounters={zeroCounters}
        todayISO={todayISO}
      />
      <NightlyRunAlert />
      <NumbersReconcile summary={summary} loading={loading} error={error} />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <E1Column summary={summary} loading={loading} error={error} />
        <JudgeColumn summary={summary} loading={loading} error={error} />
        <OperatorColumn summary={summary} loading={loading} error={error} />
      </div>

      <div className="mt-6">
        <DirectionChart summary={summary} loading={loading} error={error} />
      </div>

      <div className="mt-6">
        <TasksBlock summary={summary} loading={loading} error={error} />
      </div>
    </section>
  );
}

// Both named and default exports so callers can use either
// `import GovernanceTile from '..'` or `import { GovernanceTile } from '..'`.
export { GovernanceTile };
export default GovernanceTile;
